import React, { memo } from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

interface SocialFieldsProps {
  profile: {
    social_sms: string;
    social_call: string;
    social_telegram: string;
    social_whatsapp: string;
    use_social_icons: boolean;
    mobile_number?: string;
  };
  isEditing: boolean;
  useMobileForAllSocials: boolean;
  onSocialChange: (field: string, value: string) => void;
  onUseSocialIconsChange: (value: boolean) => void;
  onUseMobileForAllSocialsChange: (checked: boolean) => void;
}

export const SocialFields = memo(({
  profile,
  isEditing,
  useMobileForAllSocials,
  onSocialChange,
  onUseSocialIconsChange,
  onUseMobileForAllSocialsChange,
}: SocialFieldsProps) => {
  return (
    <Card className="bg-card border border-border shadow-sm p-6 mb-8">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-foreground">Add your socials</h2>
      </div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <Label htmlFor="use-mobile-for-all-socials" className="text-foreground">Use mobile number for select social icons</Label>
          <p className="text-xs text-foreground/80">Copies your mobile number into Call, Telegram and WhatsApp fields.</p>
        </div>
        <Switch
          id="use-mobile-for-all-socials"
          checked={useMobileForAllSocials}
          disabled={!isEditing}
          onCheckedChange={onUseMobileForAllSocialsChange}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="space-y-2">
          <Label htmlFor="social_sms" className="text-foreground">Emergency number</Label>
          <Input 
            id="social_sms" 
            name="social_sms" 
            type="tel" 
            value={profile.social_sms || ''} 
            onChange={(e) => onSocialChange('social_sms', e.target.value)} 
            disabled={!isEditing} 
            placeholder="Enter emergency number" 
            inputMode="numeric" 
            pattern="\\d*" 
            maxLength={15} 
            autoComplete="tel"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="social_call" className="text-foreground">Call number</Label>
          <Input 
            id="social_call" 
            name="social_call" 
            type="tel" 
            value={profile.social_call || ''} 
            onChange={(e) => onSocialChange('social_call', e.target.value)} 
            disabled={!isEditing} 
            placeholder="Enter call number" 
            inputMode="numeric" 
            pattern="\\d*" 
            maxLength={15} 
            autoComplete="tel"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="social_telegram" className="text-foreground">Telegram username/number</Label>
          <Input 
            id="social_telegram" 
            name="social_telegram" 
            type="text"
            value={profile.social_telegram || ''} 
            onChange={(e) => onSocialChange('social_telegram', e.target.value)} 
            disabled={!isEditing} 
            placeholder="Enter Telegram info" 
            maxLength={20} 
            autoComplete="off"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="social_whatsapp" className="text-foreground">WhatsApp number</Label>
          <Input 
            id="social_whatsapp" 
            name="social_whatsapp" 
            type="tel" 
            value={profile.social_whatsapp || ''} 
            onChange={(e) => onSocialChange('social_whatsapp', e.target.value)} 
            disabled={!isEditing} 
            placeholder="Enter WhatsApp number" 
            inputMode="numeric" 
            pattern="\\d*" 
            maxLength={15} 
            autoComplete="tel"
          />
        </div>
      </div>
      <div className="flex items-center gap-2 mb-2">
        <Switch 
          id="use_social_icons" 
          checked={!!profile.use_social_icons} 
          onCheckedChange={onUseSocialIconsChange} 
          disabled={!isEditing} 
        />
        <Label htmlFor="use_social_icons" className="text-foreground">Enable use of my socials in owner profile.</Label>
      </div>
    </Card>
  );
});

SocialFields.displayName = 'SocialFields';


